import type { MouseEvent } from "react";

/**
 * Overlay for a tile's clickable image gallery: left/right chevrons that
 * reveal on hover (mirrors production brand tiles, e.g.
 * faire.com/category/New%20Brands), plus a persistent dot indicator row
 * at the bottom so shoppers know there's more than one image even before
 * they hover. The parent's image container must have `group` + `relative`
 * classes for the hover reveal to work.
 */
export interface HoverGalleryControlsProps {
  imageCount: number;
  activeIndex: number;
  onChange: (index: number) => void;
}

export function HoverGalleryControls({
  imageCount,
  activeIndex,
  onChange,
}: HoverGalleryControlsProps) {
  if (imageCount <= 1) return null;

  const stopAnd =
    (next: number) =>
    (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      onChange(next);
    };

  return (
    <>
      <button
        type="button"
        aria-label="Previous image"
        onClick={stopAnd(activeIndex - 1)}
        className="bg-fs-surface-primary/90 hover:bg-fs-surface-primary absolute top-1/2 left-1.5 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100"
      >
        <ChevronIcon direction="left" />
      </button>
      <button
        type="button"
        aria-label="Next image"
        onClick={stopAnd(activeIndex + 1)}
        className="bg-fs-surface-primary/90 hover:bg-fs-surface-primary absolute top-1/2 right-1.5 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full opacity-0 shadow-sm transition-opacity duration-150 group-hover:opacity-100"
      >
        <ChevronIcon direction="right" />
      </button>
      <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1">
        {Array.from({ length: imageCount }, (_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Show image ${i + 1} of ${imageCount}`}
            aria-current={i === activeIndex}
            onClick={stopAnd(i)}
            className="p-1"
          >
            <span
              className="block h-1.5 w-1.5 rounded-full transition-colors duration-150"
              style={{
                backgroundColor:
                  i === activeIndex ? "#ffffff" : "rgba(255, 255, 255, 0.5)",
              }}
            />
          </button>
        ))}
      </div>
    </>
  );
}

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  const d =
    direction === "left"
      ? "m17 2-9.767 9.455A.77.77 0 0 0 7 12a.75.75 0 0 0 .233.545L17 22"
      : "m7 2 9.767 9.455A.77.77 0 0 1 17 12a.75.75 0 0 1-.233.545L7 22";
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d={d}
        stroke="#333333"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
